import { useEffect, useRef, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatScore } from '@/lib/format';

export type RankingBarAxis = 'dog' | 'walk_ent' | 'fresh';

export interface RankingBarProps {
  axis: RankingBarAxis;
  /** Score 0–100. `null` → empty state with strikethrough label and `—` numeric. */
  score: number | null;
  /** Hover tooltip text. */
  tooltip: string;
  /** Whether to one-shot animate fill 0→score% on first paint. */
  animate: boolean;
  className?: string;
}

const AXIS_LABEL: Record<RankingBarAxis, string> = {
  dog: 'Dog Fit',
  walk_ent: 'Walk + Ent',
  fresh: 'Freshness',
};

const AXIS_FILL_CLASS: Record<RankingBarAxis, string> = {
  dog: 'bg-tide-400',
  walk_ent: 'bg-moss-700',
  fresh: 'bg-foam-50/40',
};

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * `<RankingBar>` — one of the three always-visible breakdown bars in
 * `<PropertyCard>`.
 *
 * Anatomy (UI-SPEC § 3.5):
 *   `grid grid-cols-[88px_1fr_44px] items-center gap-3`
 *   88px label · flex-1 track (h-2 rounded-full) · 44px numeric.
 *
 * Axis fills (UI-SPEC § 1.1.1 — bronze NOT used here):
 *   dog       → `bg-tide-400`        (gate signal — cooler / distinct)
 *   walk_ent  → `bg-moss-700`        (foliage)
 *   fresh     → `bg-foam-50/40`      (low-intensity ambient)
 *
 * Mount animation (DASH-05 + § 1.4): if `animate=true` AND user does
 * NOT prefer reduced motion, fill width starts at 0% on first paint and
 * transitions to `${score}%` over 400ms `cubic-bezier(0.16, 1, 0.3, 1)`.
 * Subsequent re-renders use the static final width — no animation churn.
 *
 * Empty state (D4 cold-start): score=null → fill 0%, label gets
 * `line-through text-foam-50/40`, numeric reads `—`.
 *
 * ARIA (§ 9.3): `role="progressbar"`, `aria-valuenow|min|max`,
 * `aria-label="{axis label}: {score} of 100"`.
 */
export function RankingBar({ axis, score, tooltip, animate, className }: RankingBarProps) {
  const isEmpty = score == null;
  const finalPct = isEmpty ? 0 : Math.max(0, Math.min(100, Math.round(score)));
  const reduced = prefersReducedMotion();
  const shouldAnimate = animate && !reduced && !isEmpty;

  // Two-pass mount: first render at 0% width, then immediately set final
  // width so the CSS transition kicks in.
  const [width, setWidth] = useState<number>(shouldAnimate ? 0 : finalPct);
  const animatedRef = useRef<boolean>(false);

  useEffect(() => {
    if (animatedRef.current) {
      // Subsequent renders: snap to final without retransitioning.
      setWidth(finalPct);
      return;
    }
    if (shouldAnimate) {
      // Schedule a paint so 0→finalPct can transition.
      const id = window.requestAnimationFrame(() => {
        setWidth(finalPct);
        animatedRef.current = true;
      });
      return () => window.cancelAnimationFrame(id);
    }
    setWidth(finalPct);
    animatedRef.current = true;
    return undefined;
  }, [finalPct, shouldAnimate]);

  const axisLabel = AXIS_LABEL[axis];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="progressbar"
            aria-valuenow={isEmpty ? 0 : finalPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${axisLabel}: ${isEmpty ? 'no data' : `${finalPct} of 100`}`}
            className={cn(
              'grid grid-cols-[88px_1fr_44px] items-center gap-3 cursor-default',
              className,
            )}
          >
            <span
              className={cn(
                'text-sm font-semibold whitespace-nowrap',
                // Plan 05-06 a11y fix: empty-state was 'line-through text-foam-50/40'
                // (~3.63:1 contrast, fails axe color-contrast rule which does NOT
                // exclude line-through). Bumped to /70 (≈4.62:1) which clears the
                // 4.5:1 threshold while still reading as a de-emphasized label.
                // Active state uses full opacity foam-50.
                isEmpty ? 'line-through text-foam-50/70' : 'text-foam-50',
              )}
            >
              {axisLabel}
            </span>
            <span className="relative h-2 bg-ocean-800 rounded-full overflow-hidden block">
              <span
                aria-hidden
                className={cn(
                  'absolute left-0 top-0 h-full rounded-full',
                  AXIS_FILL_CLASS[axis],
                )}
                style={{
                  width: `${width}%`,
                  transition: shouldAnimate
                    ? 'width 400ms cubic-bezier(0.16, 1, 0.3, 1)'
                    : 'none',
                }}
              />
            </span>
            <span
              className={cn(
                'text-base font-semibold tabular-nums text-right',
                isEmpty ? 'text-foam-50/40' : 'text-foam-50',
              )}
            >
              {formatScore(score)}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          className="bg-ocean-950/95 text-foam-50 border-foam-50/10 max-w-[320px] text-xs"
          sideOffset={6}
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
